import React, { useState, useContext, useEffect, useRef } from "react";
import { View, StyleSheet, ScrollView } from "react-native";
import NtText from "../../components/NtText";
import colors from "../../config/colors";
import NtActivityIndicator from "../../components/NtActivityIndicator";
import UserContext from "../../context/UserContext";
import { useTheme } from "../../context/ThemeContext";
import NtModalUnactiveButton from "../../components/NtModalUnactiveButton";
import NtModalActiveButton from "../../components/NtModalActiveButton";
import NtModalInputField from "../../components/NtModalInputField";
import Logger from "../../common/utils/Logger";
import { parseError } from "@ntconnect/framework/pbx";
import { parseExtension } from "../utils/ExtensionUtils";
import { isMultiExtension } from "../data/multiextensiondomains";
import NtTabController from "../../components/NtTabController";
import {
  deleteExtension,
  fetchExtension,
  updateExtension,
} from "../../api/extension";
import settings from "../../config/settings";
import NtModalError from "../../components/NtModalError";
import NtTouchableEffect from "../../components/NtTouchableEffect";
import NtMaterialIcon from "../../components/NtMaterialIcon";
import { parseBoolean } from "../../common/utils/ControllerUtils";
import NtModalTitle from "../../components/NtModalTitle";
import NtModalSubtitle from "../../components/NtModalSubtitle";
import NtModalInputTitle from "../../components/NtModalInputTitle";
import NtExtensionDescriptionInputField from "./NtExtensionDescriptionInputField";
import NtModalConirmAlert from "../../components/NtModalConfirmAlert";
import NtModal from "../../components/NtModal";
import NtToggleRow from "../../components/NtToggleRow";

function NtExtensionUpdateForm({ item, onCancel, onUpdate }) {
  const { theme } = useTheme();
  const context = useContext(UserContext);
  const isMulti = isMultiExtension(context.user?.domain);
  const tabs = [
    {
      id: "phy",
      title: "Physical",
    },
    {
      id: "app",
      title: "Mobile App",
    },
  ];

  const [tab, setTab] = useState(tabs[0]);

  const [extensionPhy, setExtensionPhy] = useState(
    item.extension?.startsWith(settings.accountPrefixPhy) ? item : null,
  );
  const [extensionApp, setExtensionApp] = useState(
    item.extension?.startsWith(settings.accountPrefixApp) ? item : null,
  );

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const confirmRef = useRef();

  const isRequiredFields = () => {
    if (isMulti) {
      return (
        extensionPhy?.effective_caller_id_name &&
        extensionPhy?.password &&
        extensionPhy?.description &&
        extensionApp?.effective_caller_id_name &&
        extensionApp?.password
      );
    }

    return extensionPhy?.effective_caller_id_name && extensionPhy?.password;
  };

  const generateParams = (extension) => {
    return {
      effective_caller_id_name: extension.effective_caller_id_name || "",
      outbound_caller_id_name: extension.outbound_caller_id_name || "",
      outbound_caller_id_number: extension.outbound_caller_id_number || "",
      toll_allow: extension.toll_allow || "f",
      description: extension.description || "",
      password: extension.password,

      forward_all_enabled: extension.forward_all_enabled || false,
      forward_all_destination: extension.forward_all_destination || "",
      forward_busy_enabled: extension.forward_busy_enabled || false,
      forward_busy_destination: extension.forward_busy_destination || "",
      forward_no_answer_enabled: extension.forward_no_answer_enabled || false,
      forward_no_answer_destination:
        extension.forward_no_answer_destination || "",
      forward_user_not_registered_enabled:
        extension.forward_user_not_registered_enabled || false,
      forward_user_not_registered_destination:
        extension.forward_user_not_registered_destination || "",
    };
  };

  const renderConfirmModal = () => {
    return (
      <NtModal ref={confirmRef}>
        <NtModalConirmAlert
          title={"Delete"}
          message={
            "Are you sure you want to delete this extension? This action is permanent and cannot be undone."
          }
          onCancel={() => {
            confirmRef.current?.dismiss();
          }}
          onAccept={() => {
            confirmRef.current?.dismiss();
            handleDelete();
          }}
        />
      </NtModal>
    );
  };

  const handleDelete = async () => {
    setLoading(true);

    setError(null);

    //will update the phy first....
    let response = await deleteExtension(
      context.user?.domain,
      extensionPhy.extension,
      generateParams(extensionPhy),
    );

    Logger("Extension Phy Delete", response);

    if (!response.ok) {
      setError(parseError(response));
      setLoading(false);
      return;
    }

    if (onUpdate) {
      onUpdate();
    }

    setLoading(false);
  };

  const handleUpdate = async () => {
    setLoading(true);

    setError(null);

    //will update the phy first....
    let response = await updateExtension(
      context.user?.domain,
      extensionPhy.extension,
      generateParams(extensionPhy),
    );

    Logger("Extension Phy Patch", response);

    if (response.ok) {
      if (isMulti) {
        //will now try to update the app extension
        response = await updateExtension(
          context.user?.domain,
          extensionApp.extension,
          generateParams(extensionApp),
        );
      }
    }

    if (!response.ok) {
      setError(parseError(response));
      setLoading(false);
      return;
    }

    if (onUpdate) {
      onUpdate();
    }

    setLoading(false);
  };

  const handleExtensionInfoFetch = async () => {
    setLoading(true);

    if (!isMulti) {
      setExtensionPhy(item);
      setLoading(false);
      return;
    }

    let prefix = item.extension?.startsWith(settings.accountPrefixPhy)
      ? settings.accountPrefixApp
      : settings.accountPrefixPhy;

    const response = await fetchExtension(
      context.user?.domain,
      `${prefix}${parseExtension(item?.extension, context.user?.domain)}`,
    );

    if (response.ok) {
      Logger("Got other extension info", response);

      if (prefix == settings.accountPrefixPhy) {
        setExtensionPhy(response.data);
      } else {
        setExtensionApp(response.data);
      }
    }

    setLoading(false);
  };

  const setValue = (value) => {
    if (tab?.id == "phy") {
      setExtensionPhy({ ...extensionPhy, ...value });
    } else {
      setExtensionApp({ ...extensionApp, ...value });
    }
  };

  const getValue = (name, defaultValue = "") => {
    if (tab?.id == "phy") {
      return extensionPhy?.[name] || defaultValue;
    } else {
      return extensionApp?.[name] || defaultValue;
    }
  };

  const renderTollAllowed = (containerStyle) => {
    const enabled = getValue("toll_allow") === "t";
    return (
      <View style={containerStyle}>
        <NtToggleRow
          value={enabled}
          onChange={(next) => setValue({ toll_allow: next ? "t" : "f" })}
          label="Allow outbound calls"
          sub="When off, this extension can only place internal calls."
        />
      </View>
    );
  };

  const renderCallForwardEnabledButton = (value, onPress) => {
    return (
      <NtTouchableEffect
        style={{
          backgroundColor: value ? theme.green + "22" : theme.surface3,
          borderWidth: 1,
          borderColor: value ? theme.green + "55" : theme.borderSoft,
          padding: 5,
          borderRadius: theme.radius,
          marginRight: -6,
        }}
        onPress={() => onPress?.(!value)}
      >
        <NtMaterialIcon
          name="check-bold"
          color={value ? theme.green : theme.textMute}
          size={14}
        />
      </NtTouchableEffect>
    );
  };

  useEffect(() => {
    handleExtensionInfoFetch();
  }, []);

  return (
    <View style={styles.container}>
      <NtModalTitle>Update Extension</NtModalTitle>
      <NtModalSubtitle style={[{ marginTop: 4 }]}>
        Update extension{" "}
        <NtText style={{ fontWeight: "900" }}>
          {parseExtension(item?.extension, context.user?.domain)}
        </NtText>
      </NtModalSubtitle>

      {isMulti && (
        <NtTabController
          containerStyle={{ marginTop: 10, marginBottom: 20 }}
          options={tabs}
          selection={tab}
          onPress={(item) => {
            setTab(item);
          }}
        />
      )}

      <ScrollView
        scrollEnabled={true}
        contentContainerStyle={{ flexGrow: 1 }}
        showsVerticalScrollIndicator={false}
      >
        <NtModalInputField
          containerStyle={{ marginTop: 15 }}
          title={"Caller Name *"}
          placeholder={'e.g. "Front Desk", "Bridge Office", "John Smith"'}
          value={getValue("effective_caller_id_name")}
          setValue={(text) => setValue({ effective_caller_id_name: text })}
          footer={"Caller ID name shown on internal calls."}
        />
        <NtModalInputField
          containerStyle={{ marginTop: 15 }}
          titleStyle={{ fontWeight: "400" }}
          title={"Outbound Caller Name"}
          placeholder={'e.g. "Acme Hotel"'}
          value={getValue("outbound_caller_id_name")}
          setValue={(text) => setValue({ outbound_caller_id_name: text })}
          footer={"Optional. Name presented on outbound calls (where supported by the carrier)."}
        />
        <NtModalInputField
          containerStyle={{ marginTop: 15 }}
          title={"Outbound Caller Number"}
          titleStyle={{ fontWeight: "400" }}
          placeholder={"e.g. 13055551234"}
          value={getValue("outbound_caller_id_number")}
          setValue={(text) => setValue({ outbound_caller_id_number: text })}
          footer={"DID presented on outbound calls. Country code, no formatting."}
        />

        <NtModalInputField
          containerStyle={{ marginTop: 15 }}
          title={"Password *"}
          placeholder={"SIP password"}
          value={getValue("password")}
          setValue={(text) => setValue({ password: text })}
          footer={"Password the SIP device uses to register. Update only if you need to rotate it."}
        />

        <NtExtensionDescriptionInputField
          containerStyle={{ marginTop: 15 }}
          title={`Description ${tab?.id == "phy" ? "*" : ""}`}
          value={getValue("description")}
          setValue={(text) => setValue({ description: text })}
        />

        <View style={{ marginTop: 15 }}>
          <NtModalInputTitle style={{ marginLeft: 4, marginBottom: 6 }}>
            Call Forward
          </NtModalInputTitle>

          <View
            style={{
              paddingHorizontal: 14,
              paddingVertical: 10,
              backgroundColor: theme.surface3,
              borderWidth: 1,
              borderColor: theme.borderSoft,
              borderRadius: theme.radius,
            }}
          >
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                marginTop: 25,
              }}
            >
              <NtModalInputField
                containerStyle={{ flex: 1 }}
                title={"Forward All"}
                placeholder={"Destination"}
                value={getValue("forward_all_destination")}
                setValue={(text) => {
                  setValue({
                    forward_all_enabled: text?.length > 0,
                    forward_all_destination: text,
                  });
                }}
                renderAction={renderCallForwardEnabledButton(
                  typeof getValue("forward_all_enabled", false) === "string"
                    ? parseBoolean(getValue("forward_all_enabled", false))
                    : getValue("forward_all_enabled", false),
                  (value) => {
                    setValue({ forward_all_enabled: value });
                  },
                )}
              />

              <NtModalInputField
                containerStyle={{ flex: 1, marginLeft: 10 }}
                title={"Forward if in Use"}
                placeholder={"Destination"}
                value={getValue("forward_busy_destination")}
                setValue={(text) => {
                  setValue({
                    forward_busy_enabled: text?.length > 0,
                    forward_busy_destination: text,
                  });
                }}
                renderAction={renderCallForwardEnabledButton(
                  typeof getValue("forward_busy_enabled", false) === "string"
                    ? parseBoolean(getValue("forward_busy_enabled", false))
                    : getValue("forward_busy_enabled", false),
                  (value) => {
                    setValue({ forward_busy_enabled: value });
                  },
                )}
              />
            </View>
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                marginTop: 25,
                marginBottom: 30,
              }}
            >
              <NtModalInputField
                containerStyle={{ flex: 1 }}
                title={"Forward if not Answered"}
                placeholder={"Destination"}
                value={getValue("forward_no_answer_destination")}
                setValue={(text) => {
                  setValue({
                    forward_no_answer_enabled: text?.length > 0,
                    forward_no_answer_destination: text,
                  });
                }}
                renderAction={renderCallForwardEnabledButton(
                  typeof getValue("forward_no_answer_enabled", false) ===
                    "string"
                    ? parseBoolean(getValue("forward_no_answer_enabled", false))
                    : getValue("forward_no_answer_enabled", false),
                  (value) => {
                    setValue({ forward_no_answer_enabled: value });
                  },
                )}
              />

              <NtModalInputField
                containerStyle={{ flex: 1, marginLeft: 10 }}
                title={"Forward if not Registered"}
                placeholder={"Destination"}
                value={getValue("forward_user_not_registered_destination")}
                setValue={(text) => {
                  setValue({
                    forward_user_not_registered_enabled: text?.length > 0,
                    forward_user_not_registered_destination: text,
                  });
                }}
                renderAction={renderCallForwardEnabledButton(
                  typeof getValue(
                    "forward_user_not_registered_enabled",
                    false,
                  ) === "string"
                    ? parseBoolean(
                        getValue("forward_user_not_registered_enabled", false),
                      )
                    : getValue("forward_user_not_registered_enabled", false),
                  (value) => {
                    setValue({ forward_user_not_registered_enabled: value });
                  },
                )}
              />
            </View>
          </View>
        </View>

        {renderTollAllowed({ marginTop: 15 })}

        <NtModalError error={error} containerStyle={{ marginTop: 6 }} />
      </ScrollView>

      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          marginTop: 25,
        }}
      >
        <NtModalActiveButton
          containerStyle={{ backgroundColor: theme.red }}
          title={"Delete"}
          icon={"delete"}
          onPress={() => {
            confirmRef.current?.show();
          }}
          enabled={true}
        />

        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {loading && (
            <NtActivityIndicator
              color={colors.blue}
              size="small"
              containerStyle={{ marginRight: 25 }}
            />
          )}

          <NtModalUnactiveButton title={"Cancel"} onPress={onCancel} />

          <NtModalActiveButton
            containerStyle={{ marginLeft: 15 }}
            title={"Update"}
            onPress={() => {
              if (isRequiredFields()) {
                handleUpdate();
              }
            }}
            enabled={isRequiredFields()}
          />
        </View>
      </View>
      {renderConfirmModal()}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    minWidth: 500,
    maxWidth: 600,
    padding: 15,
  },
  title: {
    color: colors.darkest,
    fontSize: 15,
    fontWeight: "600",
  },
  subtitle: {
    color: colors.darker,
    fontSize: 14,
    fontWeight: "500",
  },
});

export default NtExtensionUpdateForm;
